import React, { useState } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Checkbox } from 'primereact/checkbox';
import { Calendar } from 'primereact/calendar';
import { Divider } from 'primereact/divider';
import { Message } from 'primereact/message';
import { ColumnInfo, WhereCondition, formatOperatorDisplay } from '../../../utils/exportUtils';

// Função auxiliar para toLowerCase seguro
const safeToLowerCase = (value: any): string => {
  return String(value || '').toLowerCase();
};

interface FilterBuilderProps {
  availableColumns: ColumnInfo[];
  conditions: WhereCondition[];
  onConditionsChange: (conditions: WhereCondition[]) => void;
}

export const FilterBuilder: React.FC<FilterBuilderProps> = ({
  availableColumns,
  conditions,
  onConditionsChange
}) => {
  const [newCondition, setNewCondition] = useState<Partial<WhereCondition>>({
    column: '',
    operator: 'equals',
    value: ''
  });

  // Verificar se temos colunas válidas
  if (!availableColumns || availableColumns.length === 0) {
    return (
      <div className="text-center p-4">
        <i className="pi pi-spin pi-spinner text-2xl text-primary"></i>
        <p className="mt-2">Carregando colunas disponíveis...</p>
      </div>
    );
  }

  const operatorOptions = [
    { label: formatOperatorDisplay('equals'), value: 'equals' },
    { label: formatOperatorDisplay('not equals'), value: 'not equals' },
    { label: formatOperatorDisplay('contains'), value: 'contains' },
    { label: formatOperatorDisplay('not contains'), value: 'not contains' },
    { label: formatOperatorDisplay('greater than'), value: 'greater than' },
    { label: formatOperatorDisplay('less than'), value: 'less than' },
    { label: formatOperatorDisplay('greater than or equal'), value: 'greater than or equal' },
    { label: formatOperatorDisplay('less than or equal'), value: 'less than or equal' },
    { label: formatOperatorDisplay('is null'), value: 'is null' },
    { label: formatOperatorDisplay('is not null'), value: 'is not null' }
  ];

  const validColumns = availableColumns.filter(col => 
    col && 
    col.name && 
    col.name !== 'undefined' && 
    String(col.name).trim() !== ''
  );

  const columnOptions = validColumns.map(col => ({
    label: col.displayName || col.name || 'Coluna sem nome',
    value: col.name,
    type: col.type || 'string'
  }));

  const addCondition = () => {
    if (newCondition.column && newCondition.operator) {
      const condition: WhereCondition = {
        column: newCondition.column,
        operator: newCondition.operator as WhereCondition['operator'],
        value: newCondition.value || ''
      };
      
      onConditionsChange([...conditions, condition]);
      setNewCondition({ column: '', operator: 'equals', value: '' });
    }
  };

  const removeCondition = (index: number) => {
    const newConditions = conditions.filter((_, i) => i !== index);
    onConditionsChange(newConditions);
  };

  const updateCondition = (index: number, field: keyof WhereCondition, value: any) => {
    const newConditions = [...conditions];
    newConditions[index] = { ...newConditions[index], [field]: value };
    onConditionsChange(newConditions);
  };

  const getSelectedColumnType = (): string => {
    const column = availableColumns.find(col => col.name === newCondition.column);
    return column?.type || 'string';
  };

  const renderValueInput = (condition: WhereCondition | Partial<WhereCondition>, onChange: (value: any) => void, isNewCondition: boolean = false) => {
    const columnType = isNewCondition ? getSelectedColumnType() : 
      availableColumns.find(col => col.name === condition.column)?.type || 'string';
    
    const operator = condition.operator;
    
    // Operadores que não precisam de valor
    if (operator === 'is null' || operator === 'is not null') {
      return (
        <span className="text-color-secondary italic">
          Não requer valor
        </span>
      );
    }

    // Input baseado no tipo da coluna
    switch (safeToLowerCase(columnType)) {
      case 'number':
      case 'integer':
      case 'decimal':
      case 'float':
        return (
          <InputNumber
            value={condition.value as number}
            onValueChange={(e) => onChange(e.value)}
            placeholder="Digite um número"
            className="w-full"
          />
        );

      case 'date':
      case 'datetime':
        return (
          <Calendar
            value={condition.value ? new Date(condition.value as string) : null}
            onChange={(e) => onChange(e.value?.toISOString())}
            placeholder="Selecione uma data"
            className="w-full"
            showIcon
          />
        );

      case 'boolean':
        return (
          <div className="flex align-items-center gap-3">
            <div className="flex align-items-center">
              <Checkbox
                checked={condition.value === true}
                onChange={(e) => onChange(e.checked)}
              />
              <label className="ml-2">Verdadeiro</label>
            </div>
            <div className="flex align-items-center">
              <Checkbox
                checked={condition.value === false}
                onChange={(e) => onChange(!e.checked)}
              />
              <label className="ml-2">Falso</label>
            </div>
          </div>
        );

      default:
        return (
          <InputText
            value={condition.value as string}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Digite um valor"
            className="w-full"
          />
        );
    }
  };

  const renderCondition = (condition: WhereCondition, index: number) => (
    <Card key={index} className="mb-2 bg-bluegray-50">
      <div className="grid align-items-center">
        <div className="col-12 md:col-3">
          <label className="block text-sm font-semibold mb-1">Coluna</label>
          <Dropdown
            value={condition.column}
            options={columnOptions}
            onChange={(e) => updateCondition(index, 'column', e.value)}
            placeholder="Selecione uma coluna"
            className="w-full"
          />
        </div>

        <div className="col-12 md:col-3">
          <label className="block text-sm font-semibold mb-1">Operador</label>
          <Dropdown
            value={condition.operator}
            options={operatorOptions}
            onChange={(e) => updateCondition(index, 'operator', e.value)}
            placeholder="Selecione um operador"
            className="w-full"
          />
        </div>

        <div className="col-12 md:col-4">
          <label className="block text-sm font-semibold mb-1">Valor</label>
          {renderValueInput(condition, (value) => updateCondition(index, 'value', value))}
        </div>

        <div className="col-12 md:col-2 text-center">
          <Button
            icon="pi pi-trash"
            className="p-button-danger p-button-outlined"
            onClick={() => removeCondition(index)}
            tooltip="Remover filtro"
          />
        </div>
      </div>
    </Card>
  );

  return (
    <div>
      <div className="flex justify-content-between align-items-center mb-3">
        <h5 className="m-0">Filtros de Dados</h5>
        <Button
          label="Limpar Todos"
          icon="pi pi-times"
          className="p-button-outlined p-button-secondary"
          onClick={() => onConditionsChange([])}
          disabled={conditions.length === 0}
        />
      </div>

      {conditions.length === 0 && (
        <Message 
          severity="info" 
          text="Nenhum filtro configurado. Os dados serão exportados sem filtros."
          className="mb-3"
        />
      )}

      {/* Filtros existentes */}
      <div className="mb-4">
        {conditions.map((condition, index) => renderCondition(condition, index))}
      </div>

      <Divider />

      {/* Adicionar novo filtro */}
      <Card title="Adicionar Novo Filtro" className="bg-surface-50">
        <div className="grid align-items-end">
          <div className="col-12 md:col-3">
            <label className="block text-sm font-semibold mb-1">Coluna</label>
            <Dropdown
              value={newCondition.column}
              options={columnOptions}
              onChange={(e) => setNewCondition({ ...newCondition, column: e.value })}
              placeholder="Selecione uma coluna"
              className="w-full"
            />
          </div>

          <div className="col-12 md:col-3">
            <label className="block text-sm font-semibold mb-1">Operador</label>
            <Dropdown
              value={newCondition.operator}
              options={operatorOptions}
              onChange={(e) => setNewCondition({ ...newCondition, operator: e.value })}
              placeholder="Selecione um operador"
              className="w-full"
            />
          </div>

          <div className="col-12 md:col-4">
            <label className="block text-sm font-semibold mb-1">Valor</label>
            {renderValueInput(
              newCondition, 
              (value) => setNewCondition({ ...newCondition, value }),
              true
            )}
          </div>

          <div className="col-12 md:col-2 text-center">
            <Button
              label="Adicionar"
              icon="pi pi-plus"
              onClick={addCondition}
              disabled={!newCondition.column || !newCondition.operator}
              className="w-full"
            />
          </div>
        </div>
      </Card>

      {conditions.length > 0 && (
        <div className="mt-3 p-3 bg-primary-50 border-round">
          <h6 className="mt-0 mb-2">
            <i className="pi pi-info-circle mr-2" />
            Resumo dos Filtros
          </h6>
          <ul className="list-none p-0 m-0">
            {conditions.map((condition, index) => {
              const column = availableColumns.find(col => col.name === condition.column);
              const columnName = column?.displayName || condition.column;
              
              return (
                <li key={index} className="mb-1">
                  <strong>{columnName}</strong> {formatOperatorDisplay(condition.operator)}
                  {condition.operator !== 'is null' && condition.operator !== 'is not null' && (
                    <> "<em>{condition.value}</em>"</>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};